"use client"

import { useEffect, useState } from "react"
import { rawTaskTitle, taskId } from "@/lib/tasks"
import { useForm, Controller } from "react-hook-form"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import {
  TaskDescriptionField,
  TaskDueDateField,
  TaskPriorityField,
  TaskTitleField,
  type TaskPriority,
} from "@/components/tasks/task-fields"
import { format } from "date-fns"
import { Trash2 } from "lucide-react"
import { Task } from "openapi/api/model"
import { toast } from "sonner"

interface EditTaskDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: Task | null
  /*
    May return a promise, and the editor waits for it: it closes only once the
    write has landed. A refusal leaves it open with the edit still in it — the
    caller is expected to have said why (the ops board toasts and rethrows).
  */
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void | Promise<unknown>
  onDeleteTask?: (taskId: string) => void | Promise<unknown>
}

export function EditTaskDialog({ open, onOpenChange, task, onUpdateTask,onDeleteTask }: EditTaskDialogProps) {
  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      title: "",
      description: "",
      priority: "medium" as "low" | "medium" | "high",
      dueDate: undefined as Date | undefined,
      dueDateOpen: false,
    },
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (open && task) {
      let localDate: Date | undefined = undefined
      if (task.due_date) {
        // If the dueDate is a full timestamp (e.g., "2025-11-30T16:30:00+00:00"), parse it
        // If it's just a date string (e.g., "2025-11-30"), append time to parse correctly
        const dateStr = task.due_date.includes("T") ? task.due_date : `${task.due_date}T00:00:00`
        localDate = new Date(dateStr)
      }

      reset({
        /*
          Either name the API sends. This read `task_title` alone, so a task
          that came back carrying `title` — which is what `POST /tasks` is given
          and what the model declares first — opened this dialog with an empty
          title field, and saving it wrote that emptiness back.
        */
        title: rawTaskTitle(task),
        description: task.description,
        priority: task.priority,
        dueDate: localDate,
        dueDateOpen: false,
      })
    }
  }, [open, task, reset])

  /** Whether a write is in flight, so a second press cannot send it twice. */
  const [saving, setSaving] = useState(false)

  /*
    Run a write and close only if it lands. The caller has already told the
    user about a refusal, so the error is kept here rather than rethrown —
    rethrowing from an event handler is an unhandled rejection, which is what
    the fire-and-forget version produced on every failed delete.
  */
  const commit = async (write: () => void | Promise<unknown>) => {
    setSaving(true)
    try {
      await write()
      onOpenChange(false)
      return true
    } catch {
      return false
    } finally {
      setSaving(false)
    }
  }

  const onSubmit = async (data: any) => {
    if (!task || saving) return

    if (data.dueDate) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const selectedDate = new Date(data.dueDate)
      selectedDate.setHours(0, 0, 0, 0)

      if (selectedDate < today) {
        toast.error('Cannot set due date to past date')
        return
      }
    }


    /*
      `taskId`, not `task_id`: a row can carry either name, and the board keys
      its cards on `id ?? task_id`. Reading `task_id` alone opened a card with
      only `id` and then saved it to `/tasks/undefined`.
    */
    const id = taskId(task)
    if (!id) return

    await commit(() =>
      onUpdateTask(id, {
        /*
          Omitted when empty rather than sent blank: a task that never had a
          title keeps not having one while its priority or date is changed.
          It is not `required` for the same reason — that only made saving any
          other field on an untitled task do nothing at all, with no error.
        */
        ...(data.title?.trim() ? { title: data.title.trim() } : {}),
        description: data.description,
        priority: data.priority,
        due_date: data.dueDate ? format(data.dueDate, "yyyy-MM-dd") : task.due_date,
      }),
    )
  }

  const handleCancel = () => {
    onOpenChange(false)
  }


  const handleDelete = async () => {
    if (!task || !onDeleteTask || saving) return

    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true)
      return
    }

    const id = taskId(task)
    if (!id) return
    if (await commit(() => onDeleteTask(id))) setShowDeleteConfirm(false)
  }

  if (!task) return null

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Task"
      footer={
        <>
          {onDeleteTask && (
            /*
              Destructive, and first in the row so it sits away from the two
              buttons you reach for by habit. It asks twice: the first press
              turns it into its own confirmation rather than opening a second
              dialog over this one.
            */
            <QuietAction
              size="md"
              tone="destructive"
              onClick={handleDelete}
              disabled={saving}
              className="@sm:mr-auto"
            >
              <Trash2 className="size-[14px]" />
              {showDeleteConfirm ? "Confirm delete" : "Delete Task"}
            </QuietAction>
          )}
          <QuietAction size="md" onClick={handleCancel} disabled={saving}>
            Cancel
          </QuietAction>
          <PageAction tone="primary" type="submit" form="edit-task-form" disabled={saving}>
            Update Task
          </PageAction>
        </>
      }
    >
      <form id="edit-task-form" onSubmit={handleSubmit(onSubmit)}>
        <TaskTitleField field={register("title")} />
        <TaskDescriptionField field={register("description")} />

        <div className="flex flex-col gap-0 @sm:flex-row @sm:gap-6">
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <TaskPriorityField
                value={(field.value as TaskPriority) ?? "medium"}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="dueDate"
            control={control}
            render={({ field }) => (
              <Controller
                name="dueDateOpen"
                control={control}
                render={({ field: openField }) => (
                  <TaskDueDateField
                    value={field.value}
                    onChange={field.onChange}
                    open={Boolean(openField.value)}
                    onOpenChange={openField.onChange}
                  />
                )}
              />
            )}
          />
        </div>
      </form>
    </DialogShell>
  )
}
